import {
  PDF_INFO_BLOCK_FIXED,
  PROCESSED_ORDER_MOCK,
} from "./processedOrderMockData";

function firstString(obj, keys, fallback) {
  if (!obj || typeof obj !== "object") return fallback;
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = obj[k];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return fallback;
}

function firstNumber(obj, keys, fallback) {
  if (!obj || typeof obj !== "object") return fallback;
  for (let i = 0; i < keys.length; i += 1) {
    const v = obj[keys[i]];
    if (v != null && v !== "") {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return fallback;
}

/**
 * Format date-like values for display (ISO or passthrough string).
 * @param {unknown} raw
 * @returns {string}
 */
function formatDisplayDate(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    try {
      return new Intl.DateTimeFormat("sk-SK", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
    } catch (_e) {
      return s;
    }
  }
  return s;
}

/**
 * Unwrap typical API envelopes.
 * @param {unknown} raw
 * @returns {object | null}
 */
function unwrapPayload(raw) {
  if (raw == null) return null;
  if (typeof raw !== "object") return null;
  if (raw.success === false) return null;
  const inner = raw.data ?? raw.response ?? raw.payload ?? raw;
  if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
    return inner;
  }
  return typeof raw === "object" && !Array.isArray(raw) ? raw : null;
}

/**
 * Total ticket count = sum of `quantity` across all product lines (backend `products[]`).
 * @param {unknown} products
 * @returns {number | null} null if no products array to sum
 */
function ticketCountFromProducts(products) {
  if (!Array.isArray(products) || products.length === 0) return null;
  return products.reduce((acc, p) => acc + (Number(p?.quantity) || 0), 0);
}

/**
 * @typedef {{ title: string; quantity: number }} TicketLineItem
 */

/**
 * @param {unknown} products
 * @returns {TicketLineItem[]}
 */
function buildTicketLinesFromProducts(products) {
  if (!Array.isArray(products) || products.length === 0) return [];
  return products.map((p) => {
    const q = Number(p?.quantity);
    const quantity =
      Number.isFinite(q) && q >= 0 ? Math.max(0, Math.floor(q)) : 0;
    const title =
      p?.title != null && String(p.title).trim() !== ""
        ? String(p.title).trim()
        : "—";
    return { title, quantity };
  });
}

function mapOrderStatusLabel(raw) {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (s === "paid") return "Zaplatené";
  if (s === "pending") return "Čaká sa na platbu";
  if (s === "cancelled" || s === "canceled") return "Zrušené";
  if (!s) return PROCESSED_ORDER_MOCK.orderStatusLabel;
  return String(raw);
}

/**
 * Backend-provided QR bitmap (`qrCode.image`), typically a data URL.
 * @param {object} orderPayload
 * @returns {string | null}
 */
function resolveQrCodeImageSrc(orderPayload) {
  if (orderPayload == null || typeof orderPayload !== "object") return null;
  const qc = orderPayload.qrCode;
  if (qc == null || typeof qc !== "object") return null;
  const raw = qc.image;
  if (raw == null) return null;
  const s = String(raw);
  if (s.trim() === "") return null;
  return s;
}

/**
 * Info block for the ticket: backend `pdfPrintSettings.infoBlock` when set, else default copy.
 * Value is passed through as returned (no trim); only null/undefined/"" use the fallback.
 *
 * @param {object | null} pdfPrintSettings
 * @param {boolean} hasPdfSettings
 * @returns {string}
 */
function resolveInfoTextBlock(pdfPrintSettings, hasPdfSettings) {
  if (
    !hasPdfSettings ||
    pdfPrintSettings == null ||
    typeof pdfPrintSettings !== "object"
  ) {
    return PDF_INFO_BLOCK_FIXED;
  }
  const raw = pdfPrintSettings.infoBlock;
  if (raw == null) return PDF_INFO_BLOCK_FIXED;
  if (typeof raw === "string" && raw.length === 0) {
    return PDF_INFO_BLOCK_FIXED;
  }
  return String(raw);
}

/**
 * PDF/print footer: when `accessSystem.pdfPrintSettings` includes `footerText`, that value wins
 * (empty string = no footer). Otherwise order-level `footerText` / `footer` / mock.
 *
 * @param {object | null} pdfPrintSettings
 * @param {boolean} hasPdfSettings
 * @param {object} orderPayload
 * @param {string} mockFooter
 * @returns {string}
 */
function resolveFooterText(
  pdfPrintSettings,
  hasPdfSettings,
  orderPayload,
  mockFooter,
) {
  if (
    hasPdfSettings &&
    pdfPrintSettings != null &&
    typeof pdfPrintSettings === "object" &&
    Object.prototype.hasOwnProperty.call(pdfPrintSettings, "footerText")
  ) {
    const raw = pdfPrintSettings.footerText;
    if (raw == null) return "";
    return String(raw).trim();
  }
  return firstString(orderPayload, ["footerText", "footer"], mockFooter);
}

/**
 * Thank-you title: `pdfPrintSettings.thankYouTitle` when present on settings object;
 * empty string hides title. Otherwise order-level fields / mock.
 *
 * @param {object | null} pdfPrintSettings
 * @param {boolean} hasPdfSettings
 * @param {object} orderPayload
 * @param {string} mockTitle
 * @returns {string}
 */
function resolveThankYouTitle(
  pdfPrintSettings,
  hasPdfSettings,
  orderPayload,
  mockTitle,
) {
  if (
    hasPdfSettings &&
    pdfPrintSettings != null &&
    typeof pdfPrintSettings === "object" &&
    Object.prototype.hasOwnProperty.call(pdfPrintSettings, "thankYouTitle")
  ) {
    const raw = pdfPrintSettings.thankYouTitle;
    if (raw == null) return "";
    return String(raw).trim();
  }
  return firstString(orderPayload, ["thankYouTitle", "title"], mockTitle);
}

/**
 * Thank-you description (HTML): `pdfPrintSettings.thankYouDescription` when key present;
 * empty string hides block. Otherwise order-level fields / mock.
 *
 * @param {object | null} pdfPrintSettings
 * @param {boolean} hasPdfSettings
 * @param {object} orderPayload
 * @param {string} mockDescription
 * @returns {string}
 */
function resolveThankYouDescription(
  pdfPrintSettings,
  hasPdfSettings,
  orderPayload,
  mockDescription,
) {
  if (
    hasPdfSettings &&
    pdfPrintSettings != null &&
    typeof pdfPrintSettings === "object" &&
    Object.prototype.hasOwnProperty.call(
      pdfPrintSettings,
      "thankYouDescription",
    )
  ) {
    const raw = pdfPrintSettings.thankYouDescription;
    if (raw == null) return "";
    return String(raw);
  }
  return firstString(
    orderPayload,
    ["thankYouDescription", "description", "message"],
    mockDescription,
  );
}

/**
 * Maps GET /api/v1/processed-order/by-shopify-auth-url response to the print + screen view model.
 * Uses `accessSystem.pdfPrintSettings` for PDF logo and bottom image when present; otherwise legacy fallbacks.
 *
 * @param {unknown} apiResponse
 * @returns {object | null}
 */
export function mapProcessedOrderApiToViewModel(apiResponse) {
  const d = unwrapPayload(apiResponse);
  if (!d) return null;

  const m = PROCESSED_ORDER_MOCK;

  const access =
    d.accessSystem != null && typeof d.accessSystem === "object"
      ? d.accessSystem
      : null;
  const pdf =
    access?.pdfPrintSettings != null &&
    typeof access.pdfPrintSettings === "object"
      ? access.pdfPrintSettings
      : null;

  const hasPdfSettings = Boolean(access && pdf);

  let printHeaderImageUrl;
  let printBottomImageUrl;

  if (hasPdfSettings) {
    const logo = pdf.logoUrl != null ? String(pdf.logoUrl).trim() : "";
    const bottom =
      pdf.bottomImageUrl != null ? String(pdf.bottomImageUrl).trim() : "";
    printHeaderImageUrl = logo !== "" ? logo : null;
    printBottomImageUrl = bottom !== "" ? bottom : null;
  } else {
    printHeaderImageUrl = firstString(
      d,
      ["customerLogoUrl", "logoUrl", "logo", "customer_logo_url"],
      m.customerLogoUrl,
    );
    printBottomImageUrl = firstString(
      d,
      ["bottomImageUrl", "bottom_image_url", "mapImageUrl", "map_url"],
      m.bottomImageUrl,
    );
  }

  const orderCode = firstString(
    d,
    ["orderCode", "code", "orderId", "order_id", "id"],
    m.fallbackOrderCode,
  );

  const internalNum = d.internalOrderNumber;
  let orderNumber = "";
  if (internalNum != null && String(internalNum).trim() !== "") {
    orderNumber = String(internalNum).trim();
  } else {
    orderNumber =
      firstString(
        d,
        ["orderNumber", "order_number", "name", "orderId", "order_id"],
        "",
      ) || orderCode;
  }

  const productsCount = ticketCountFromProducts(d.products);
  const ticketLines = buildTicketLinesFromProducts(d.products);
  const ticketCount =
    productsCount != null
      ? productsCount
      : firstNumber(
          d,
          ["ticketCount", "tickets_count", "tickets", "count"],
          m.ticketCount,
        );

  const orderStatusRaw = d.orderStatus ?? d.order_status ?? d.status;
  const orderStatusLabel = mapOrderStatusLabel(orderStatusRaw);

  const createdRaw =
    d.createdAt ??
    d.orderCreatedAtDisplay ??
    d.createdAtDisplay ??
    d.created_at ??
    d.date;
  let orderCreatedAtDisplay = m.orderCreatedAtDisplay;
  if (createdRaw != null && String(createdRaw).trim() !== "") {
    const formatted = formatDisplayDate(createdRaw);
    if (formatted) orderCreatedAtDisplay = formatted;
  }

  const qrNested =
    d.qrCode != null && typeof d.qrCode === "object" ? d.qrCode : null;
  const qrCodeImageSrc = resolveQrCodeImageSrc(d);

  /** Value encoded in the ticket QR; shown as "Kód objednávky" on print — must match `qrCode.value`. */
  let qrCodeValue = "";
  if (
    qrNested &&
    qrNested.value != null &&
    String(qrNested.value).trim() !== ""
  ) {
    qrCodeValue = String(qrNested.value).trim();
  } else {
    const flat = firstString(d, ["qrCodeValue", "qr_value", "qr"], "");
    if (flat) {
      qrCodeValue = flat;
    }
  }

  const screenLogoUrl = hasPdfSettings
    ? printHeaderImageUrl
    : firstString(
        d,
        [
          "screenLogoUrl",
          "screen_logo_url",
          "customerLogoUrl",
          "logoUrl",
          "logo",
        ],
        m.screenLogoUrl,
      );

  return {
    ...m,
    orderCode,
    orderNumber,
    customerLogoUrl: printHeaderImageUrl,
    screenLogoUrl,
    printHeaderImageUrl,
    printBottomImageUrl,
    qrCodeImageSrc,
    qrCodeValue,
    thankYouTitle: resolveThankYouTitle(pdf, hasPdfSettings, d, m.thankYouTitle),
    thankYouDescription: resolveThankYouDescription(
      pdf,
      hasPdfSettings,
      d,
      m.thankYouDescription,
    ),
    printButtonLabel: firstString(
      d,
      ["printButtonLabel", "print_label"],
      m.printButtonLabel,
    ),
    infoTextBlock: resolveInfoTextBlock(pdf, hasPdfSettings),
    orderCreatedAtDisplay,
    ticketCount,
    ticketLines,
    orderStatusLabel,
    footerText: resolveFooterText(pdf, hasPdfSettings, d, m.footerText),
    bottomImageUrl: printBottomImageUrl,
    invalidOrderCode: false,
  };
}
